import { getPlatformDisplayName } from 'src/utils/platform-label';

/**
 * Whether the installed Voolist extension can work on a marketplace.
 *
 * Every extension build works on Poshmark and Depop. A marketplace added later is known only to the
 * builds released with it, and Chrome can keep a seller on an older build for days after a release.
 * So before the web app asks the browser to act on such a marketplace — connect, publish, take down,
 * pull, import — it asks the extension (voolistExtension.knowsMarketplace), and an older build gets
 * one plain sentence telling the seller to update, instead of a failure recorded on the listing.
 *
 * The extension names what it can do among its capabilities (voolistExtension.getCapabilities).
 */

/** The capability a build names when it can work on a marketplace that older builds cannot. */
const CAPABILITY_FOR_MARKETPLACE = Object.freeze({
  grailed: 'grailed',
  whatnot: 'whatnot',
  offerup: 'offerup',
  vestiaire: 'vestiaire',
});

/** Whether every extension build works on this marketplace, so there is nothing to ask. */
export function everyExtensionKnowsMarketplace(platform) {
  return !CAPABILITY_FOR_MARKETPLACE[platform];
}

/**
 * @param {string[]|null} capabilities - What the extension answered it can do; null when no
 *   extension answered
 * @param {string} platform
 * @returns {boolean} False only when an extension answered without naming this marketplace. When
 *   none answered, nothing is known, and saying so is the availability check's job, not this one's.
 */
export function extensionKnowsMarketplace(capabilities, platform) {
  if (everyExtensionKnowsMarketplace(platform) || !Array.isArray(capabilities)) return true;
  return capabilities.includes(CAPABILITY_FOR_MARKETPLACE[platform]);
}

/** What a seller reads when their extension is too old for this marketplace. */
export function updateExtensionMessage(platform) {
  return `Update the Voolist extension to use ${getPlatformDisplayName(platform)}.`;
}
